import { Bot, ExternalLink, User } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import EmployerReviewStatusBadge from 'components/brokers/EmployerReviewStatusBadge'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SimpleFieldRow } from 'components/reusable/SimpleFieldRow'
import { adminUsers } from 'rails_routes'
import { Badge, Banner, ButtonGroup, DropdownMenu, Modal } from 'slab'
import { getReviewStatusDisplayText } from 'utils/employer_utils'
import { usePatchAdminEmployersEmployerId } from 'utils/web_api'
import { type AdminEmployer, AdminEmployerReviewStatus } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

function ReviewStatusDropdown ({
  selectedStatus,
  onStatusChange,
  zIndex,
  allowedStatuses,
  disabled = false
}: {
  selectedStatus: AdminEmployerReviewStatus | null
  onStatusChange: (status: AdminEmployerReviewStatus) => void
  zIndex?: number
  allowedStatuses?: AdminEmployerReviewStatus[]
  disabled?: boolean
}) {
  const selectedNonTerminalStatus = selectedStatus && (selectedStatus !== AdminEmployerReviewStatus.pending)

  const statusesToShow = allowedStatuses ?? Object.values(AdminEmployerReviewStatus)

  const button = (
    <button
      className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-thatch-500 focus:border-thatch-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      disabled={disabled}
    >
      {selectedNonTerminalStatus ? getReviewStatusDisplayText(selectedStatus) : 'Choose a status...'}
    </button>
  )

  if (disabled) {
    return (
      <DisplayTooltipOnHover text='You do not have permission to change the review status of this Employer.' asChild>
        <div className='w-full'>
          {button}
        </div>
      </DisplayTooltipOnHover>
    )
  }

  return (
    <DropdownMenu
      zIndex={zIndex}
      trigger={button}
    >
      {statusesToShow.map((status) => (
        <DropdownMenu.Item
          key={status}
          onSelect={() => onStatusChange(status)}
        >
          {getReviewStatusDisplayText(status)}
        </DropdownMenu.Item>
      ))}
    </DropdownMenu>
  )
}

function isDeniedStatus (status: AdminEmployerReviewStatus | undefined): boolean {
  if (!status) return false
  const deniedStatuses = [
    AdminEmployerReviewStatus.denied,
    AdminEmployerReviewStatus.inactive_duplicate,
    AdminEmployerReviewStatus.inactive_withdrawn,
    AdminEmployerReviewStatus.inactive_ineligible,
    AdminEmployerReviewStatus.inactive_churned
  ] as const
  return deniedStatuses.includes(status as typeof deniedStatuses[number])
}

function SubmitDecisionButton ({
  selectedStatus,
  employerId,
}: {
  selectedStatus: AdminEmployerReviewStatus
  employerId: string
}) {
  const { reload } = useWindowLocation()

  const patchEmployerReviewStatus = usePatchAdminEmployersEmployerId()

  return (
    <div className='mt-4'>
      <button
        className='w-full bg-thatch-500 hover:bg-thatch-600 text-white font-medium py-2 px-4 rounded-md transition-colors'
        onClick={() => {
          patchEmployerReviewStatus.mutate({
            employerId,
            data: { review_status: selectedStatus }
          }, {
            onSuccess: () => {
              reload()
            },
            onError: (error: any) => {
              console.error('Failed to submit decision:', error.message)
              toast.error(error.message)
            }
          })
        }}
        disabled={patchEmployerReviewStatus.isPending}
      >
        {patchEmployerReviewStatus.isPending ? 'Submitting...' : 'Submit Decision'}
      </button>
      <p className='text-xs text-gray-500 mt-2 text-center'>
        Status: {getReviewStatusDisplayText(selectedStatus)}
      </p>
    </div>
  )
}

interface EmployerDecisionModalProps {
  employer: AdminEmployer
}

export default function EmployerDecisionModal ({ employer }: EmployerDecisionModalProps) {
  const [selectedStatus, setSelectedStatus] = useState<AdminEmployerReviewStatus | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [changeSelectedStatus, setChangeSelectedStatus] = useState<AdminEmployerReviewStatus | null>(null)

  const { reload } = useWindowLocation()
  const patchEmployerReviewStatus = usePatchAdminEmployersEmployerId()

  const selectedNonTerminalStatus = selectedStatus && (selectedStatus !== AdminEmployerReviewStatus.pending)
  const changeSelectedNonTerminalStatus = changeSelectedStatus && (changeSelectedStatus !== AdminEmployerReviewStatus.pending)
  const decisionSubmitted = employer.review_status !== AdminEmployerReviewStatus.pending
  const employerIsDenied = isDeniedStatus(employer.review_status)
  const canWriteReviewStatus = employer.permitted_attributes?.includes('review_status') ?? false

  // When employer is denied, we don't allow changing to pending or approved
  const allowedStatusesForChange = employerIsDenied
    ? Object.values(AdminEmployerReviewStatus).filter(
      status => status !== AdminEmployerReviewStatus.approved
    )
    : undefined

  const handleChangeDecision = () => {
    if (!changeSelectedStatus) return

    patchEmployerReviewStatus.mutate({
      employerId: employer.id,
      data: { review_status: changeSelectedStatus }
    }, {
      onSuccess: () => {
        setIsModalOpen(false)
        setChangeSelectedStatus(null)
        reload()
      },
      onError: (error: any) => {
        console.error('Failed to change decision:', error.message)
        toast.error(error.message)
      }
    })
  }

  const handleCancel = () => {
    setChangeSelectedStatus(null)
    setIsModalOpen(false)
  }

  const selectDecisionView = (
    <div className='border-t border-gray-300 p-4'>
      <div>
        <label className='block text-sm font-medium text-gray-700 mb-2'>Current Status</label>
        <EmployerReviewStatusBadge shortened employer={employer} />
      </div>
      <div className='mt-4'>
        <label className='block text-sm font-medium text-gray-700 mb-2'>Select Decision</label>
        <ReviewStatusDropdown selectedStatus={selectedStatus} onStatusChange={setSelectedStatus} disabled={!canWriteReviewStatus} />
      </div>
      {selectedNonTerminalStatus && <SubmitDecisionButton selectedStatus={selectedStatus} employerId={employer.id} />}
    </div>
  )

  const submittedDecisionView = (
    <div className='border-t border-gray-300 divide-y divide-gray-100 p-4'>
      <SimpleFieldRow
        label='Decision'
        value={<EmployerReviewStatusBadge shortened employer={employer} />}
      />
      <SimpleFieldRow
        label='Reviewer'
        value={
          employer.reviewed_by_id
            ? (
              <Badge
                type='info'
                leftIcon={User}
                actionIcon={ExternalLink}
                onActionClick={() => {
                  window.open(adminUsers.show({ id: employer.reviewed_by_id }), '_blank')
                }}
              >
                {employer.reviewed_by_id.slice(-5)}
              </Badge>
              )
            : (
              <Badge
                type='info'
                leftIcon={Bot}
              >
                Auto-Approved
              </Badge>
              )
        }
      />
      <SimpleFieldRow
        label='Date'
        value={employer.reviewed_at ? new Date(employer.reviewed_at).toLocaleDateString() : 'Unknown'}
      />
      <div className='mt-4'>
        {canWriteReviewStatus
          ? (
            <button
              className='w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors'
              onClick={() => setIsModalOpen(true)}
            >
              Change Decision
            </button>
            )
          : (
            <DisplayTooltipOnHover text='You do not have permission to change the review status of this Employer.' asChild>
              <div className='w-full'>
                <button
                  className='w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-md transition-colors opacity-50 cursor-not-allowed'
                  disabled
                >
                  Change Decision
                </button>
              </div>
            </DisplayTooltipOnHover>
            )}
      </div>
    </div>
  )

  return (
    <>
      <div className='border border-gray-400 rounded-lg shadow-md'>
        <div className='flex text-left items-start w-full body text-gray-800 p-4 gap-2 outline outline-2 outline-transparent focus-visible:outline-thatch-500 rounded-lg'>
          <span className='heading-md'>Review Decision</span>
        </div>
        {decisionSubmitted ? submittedDecisionView : selectDecisionView}
      </div>

      <Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Modal.Body>
          <Modal.Title>Change Decision</Modal.Title>
          <Modal.Description>Update the review status for this employer.</Modal.Description>
          {employerIsDenied && (
            <Banner type='critical' title='Employer is in denied status.'>
              If you need to change this decision to Approved or Pending Review, please contact Engineering.
            </Banner>
          )}
          <div className='space-y-4 mt-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>Current Decision</label>
              <EmployerReviewStatusBadge employer={employer} />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>New Decision</label>
              <ReviewStatusDropdown selectedStatus={changeSelectedStatus} onStatusChange={setChangeSelectedStatus} zIndex={100} allowedStatuses={allowedStatusesForChange} disabled={!canWriteReviewStatus} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Modal.Button variant='secondary' onClick={handleCancel} disabled={patchEmployerReviewStatus.isPending}>
              Cancel
            </Modal.Button>
            <Modal.Button
              variant='primary'
              onClick={handleChangeDecision}
              disabled={!changeSelectedNonTerminalStatus || patchEmployerReviewStatus.isPending}
            >
              {patchEmployerReviewStatus.isPending ? 'Submitting...' : 'Submit Changed Decision'}
            </Modal.Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>
    </>
  )
}
